/* eslint-disable react/jsx-filename-extension */
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, IonIcon, Spinner } from 'controls'
import styles from './styles'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

const SCALE_STEP = 0.05

const PDFViewer = ({ file, onCompleteScroll, children, onCustomFullscreen }) => {
  const [state, setState] = useState(() => ({
    loading: true,
    error: null,
    originalURL: file,
    pdfBlobUrl: null,
  }))

  const {
    loading,
    error,
    pdfBlobUrl
  } = state

  const [numPages, setNumPages] = useState(null);
  const [pageSize, setPageSize] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasScrollCompleted, setHasScrollCompleted] = useState(false);
  const [hasFullyLoaded, setHasFullyLoaded] = useState(false);

  const pdfViewerContainerRef = useRef(null);
  const pdfContentRef = useRef(null);

  const calculatePageSize = useCallback(() => {
    const containerWidth = pdfViewerContainerRef?.current?.querySelector('.pdf-viewer-content > div').getBoundingClientRect().width;
    const canvasWidth = pdfViewerContainerRef?.current?.querySelector('canvas')?.getBoundingClientRect().width;

    if (containerWidth && canvasWidth) {
      const rawScale = containerWidth / canvasWidth;
      const fittedScale = Number((Math.floor(rawScale / SCALE_STEP) * SCALE_STEP).toFixed(2));
      setPageSize(Number(fittedScale));
    }
  }, []);

  function onDocumentLoadSuccess({ numPages: pagesCount }) {
    setNumPages(pagesCount);
    setHasFullyLoaded(true);
  }

  function onLoadProgress(progressData) {
    // Optional: Handle load progress if needed
    // console.log('Loading progress:', progressData);
  }

  const onPageLoadSuccess = (page) => {
    if (page._pageIndex !== 0) {
      return
    }
    calculatePageSize()
  };

  function increasePageSize() {
    if (pageSize >= 2) {
      return
    }

    setPageSize((prev) => prev + SCALE_STEP)
  }

  function decreasePageSize() {
    if (pageSize <= 0.3) {
      return
    }

    setPageSize((prev) => prev - SCALE_STEP)
  }

  const checkPDFLoad = useCallback(() => {
    setPageSize(1)
    setState((prev) => ({ ...prev, loading: true, error: null }))

    fetch(file, { method: 'GET' })
      .then((response) => {
        if (response.ok) {
          return response.blob()
        }
        throw new Error('PDF not accessible')
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        setState((prev) => ({
          ...prev,
          loading: false,
          error: null,
          pdfBlobUrl: blobUrl
        }))
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'No se pudo cargar el documento PDF',
        }))
      })
  }, [file])

  const refreshPDF = useCallback(() => {
    setHasFullyLoaded(false)
    setState((prev) => ({ ...prev, loading: true }))
    const isDesktop = window.innerWidth > 992
    setTimeout(checkPDFLoad, isDesktop ? 1000 : 500)
  }, [checkPDFLoad])

  useEffect(() => {
    const handleOrientationChange = () => {
      refreshPDF()
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [checkPDFLoad]);

  const downloadPDF = useCallback(() => {
    const fileUrl = pdfBlobUrl || file
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = 'documento.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [pdfBlobUrl, file])

  const openInNewWindow = useCallback(() => {
    const fileUrl = pdfBlobUrl || file
    window.open(fileUrl, '_blank')
  }, [pdfBlobUrl, file])

  const toggleFullscreen = useCallback(() => {
    const newFullscreenState = !isFullscreen
    setIsFullscreen(newFullscreenState)
    
    if (onCustomFullscreen) {
      onCustomFullscreen(newFullscreenState)
    }

    refreshPDF()
  }, [isFullscreen, onCustomFullscreen, checkPDFLoad])

  // Function to check if scroll is at the end or no scrollbar exists
  const checkScrollComplete = useCallback(() => {
    const contentElement = pdfContentRef.current
    if (!contentElement || !onCompleteScroll || hasScrollCompleted || !hasFullyLoaded) return

    const { scrollTop, scrollHeight, clientHeight } = contentElement
    
    // Check if there's no scrollbar (content fits in container)
    const hasNoScrollbar = scrollHeight <= clientHeight
    
    // Check if scrolled to the end (with 1px tolerance for rounding)
    const isScrolledToEnd = scrollTop + clientHeight >= scrollHeight - 1
    
    if (hasNoScrollbar || isScrolledToEnd) {
      setHasScrollCompleted(true)
      onCompleteScroll()
    }
  }, [onCompleteScroll, hasScrollCompleted, hasFullyLoaded])

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkScrollComplete()
  }, [checkScrollComplete])


  // Effect for file changes and initial load
  useEffect(() => {
    // Clean up previous blob URL when file changes
    setState((prevState) => {
      if (prevState.pdfBlobUrl) {
        URL.revokeObjectURL(prevState.pdfBlobUrl)
      }
      return {
        ...prevState,
        loading: true,
        error: null,
        pdfBlobUrl: null,
      }
    })

    // Reset scroll completion state when file changes
    setHasScrollCompleted(false)
    setHasFullyLoaded(false)

    checkPDFLoad()
  }, [file, checkPDFLoad])

  // Effect to handle scroll events
  useEffect(() => {
    const contentElement = pdfContentRef.current
    if (!contentElement) return

    contentElement.addEventListener('scroll', handleScroll)
    
    return () => {
      contentElement.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // Effect to check scroll complete when PDF loads or changes
  useEffect(() => {
    if (!loading && !error && numPages && hasFullyLoaded) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        checkScrollComplete()
      }, 200)
      
      return () => clearTimeout(timeoutId)
    }
  }, [loading, error, numPages, hasFullyLoaded, checkScrollComplete])

  // Cleanup effect on unmount
  useEffect(() => () => {
    // Clean up blob URL to prevent memory leaks
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
    }
  }, [pdfBlobUrl])

  const displayUrl = pdfBlobUrl || file

  const pageSizeToDisplay = Math.round(pageSize * 100)

  let dimensionSize = 0
  if (window.innerHeight > window.innerWidth) {
    dimensionSize = window.innerHeight
  } else if (window.innerWidth > 1400) {
    dimensionSize = window.innerWidth * 0.5
  } else if (window.innerWidth > 1000) {
    dimensionSize = window.innerWidth * 0.75
  } else {
    dimensionSize = window.innerWidth
  }

  return (
    <div className={classNames("pdf-viewer-container", { "fullscreen": isFullscreen })} ref={pdfViewerContainerRef}>
      <div className="pdf-viewer-controls">
        <div className="pdf-viewer-actions">
          <Button
            type="button"
            size="small"
            outline
            onClick={downloadPDF}
            className="pdf-viewer-action-button"
          >
            <IonIcon size="small" icon="ios-download" />
            <span className="pdf-viewer-action-button-text">Descargar</span>
          </Button>

          <Button
            type="button"
            size="small"
            outline
            onClick={openInNewWindow}
            className="pdf-viewer-action-button"
          >
            <IonIcon size="small" icon="ios-open" />
            <span className="pdf-viewer-action-button-text">Abrir en nueva ventana</span>
          </Button>

          <Button
            type="button"
            size="small"
            outline
            onClick={checkPDFLoad}
            className="pdf-viewer-action-button"
          >
            <IonIcon size="small" icon="ios-refresh" />
            <span className="pdf-viewer-action-button-text">Recargar</span>
          </Button>

          <Button
            type="button"
            size="small"
            outline
            onClick={toggleFullscreen}
            className="pdf-viewer-action-button"
          >
            <IonIcon size="small" icon={isFullscreen ? 'ios-contract' : 'ios-expand'} />
            <span className="pdf-viewer-action-button-text">
              {isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            </span>
          </Button>
        </div>
        {!loading && !error && (
          <div className="pdf-viewer-zoom">
            <Button size="small" color="tertiary" onClick={decreasePageSize}>-</Button>
            <span className='pdf-viewer-percentage'>
              {pageSizeToDisplay}
              %
            </span>
            <Button size="small" color="tertiary" onClick={increasePageSize}>+</Button>
          </div>
        )}
      </div>


      <div className="pdf-viewer-content" style={{ overflow: 'auto' }} ref={pdfContentRef}>
        {loading && (
          <div className="pdf-viewer-loading">
            <Spinner size={20} />
            <span>Cargando documento...</span>
          </div>
        )}

        {error && (
          <div className="pdf-viewer-error">
            <IonIcon icon="ios-alert" />
            <span>{error}</span>
            <div className="error-actions">
              <Button
                type="button"
                size="small"
                onClick={openInNewWindow}
              >
                Abrir en nueva ventana
              </Button>
              <Button
                type="button"
                size="small"
                outline
                onClick={downloadPDF}
              >
                Descargar PDF
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div>
              <Document file={displayUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadProgress={onLoadProgress}>
                {Array.from(
                  new Array(numPages),
                  (el, index) => (
                    <Page
                      height={dimensionSize}
                      width={dimensionSize}
                      scale={pageSize}
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      onLoadSuccess={onPageLoadSuccess}
                    />
                  ),
                )}
              </Document>
            </div>
          </>
        )}
      </div>

      {children}

      <style jsx>{styles}</style>
    </div>
  )
}

PDFViewer.propTypes = {
  file: PropTypes.string.isRequired,
  onCompleteScroll: PropTypes.func,
  children: PropTypes.node,
  onCustomFullscreen: PropTypes.func,
}

export default PDFViewer
